/**
 * MockDetector: Auto-Fix Suggestion Generator
 * 
 * Generates specific, actionable fix suggestions for detected mock data violations.
 */

import { MockViolation } from '../types/mockdetector.types';

export interface AutoFixSuggestion {
  violation: MockViolation;
  fixType: 'database_query' | 'api_call' | 'state_hook' | 'prop_passing' | 'constants_file';
  difficulty: 'easy' | 'medium' | 'hard';
  confidence: number; // 0-100, how confident we are this fix will work
  codeSnippet: string;
  explanation: string;
  estimatedTime: string; // "5 min", "15 min", "30 min"
  requiredFiles?: string[]; // Files that need to be created/modified
}

/**
 * Analyze violation context and generate specific fix suggestion
 */
export const generateAutoFix = (violation: MockViolation): AutoFixSuggestion => {
  const code = violation.code || '';
  const description = violation.description.toLowerCase();
  
  // Analyze violation type and generate appropriate fix
  
  // Case 1: useState with hardcoded array
  if (code.includes('useState') && code.includes('[')) {
    return {
      violation,
      fixType: 'database_query',
      difficulty: 'easy',
      confidence: 90,
      estimatedTime: '10 min',
      codeSnippet: generateDatabaseFetchSnippet(violation),
      explanation: 'Replace hardcoded array with data fetched from Supabase. This ensures your app displays real, up-to-date information.',
      requiredFiles: []
    };
  }
  
  // Case 2: useState with hardcoded number
  if (code.includes('useState') && /\d+/.test(code)) {
    return {
      violation,
      fixType: 'database_query',
      difficulty: 'easy',
      confidence: 85,
      estimatedTime: '5 min',
      codeSnippet: generateCountQuerySnippet(violation),
      explanation: 'Replace hardcoded count with a real database count query. This provides accurate metrics.',
      requiredFiles: []
    };
  }
  
  // Case 3: .map() with inline array
  if (code.includes('.map(') || description.includes('map')) {
    return {
      violation,
      fixType: 'state_hook',
      difficulty: 'medium',
      confidence: 75,
      estimatedTime: '15 min',
      codeSnippet: generateStateBasedMapSnippet(violation),
      explanation: 'Convert inline array to state-based data loading. This allows dynamic content updates.',
      requiredFiles: []
    };
  }
  
  // Case 4: Hardcoded numbers in UI
  if (/\d{2,}/.test(code) && !code.includes('useState')) {
    return {
      violation,
      fixType: 'prop_passing',
      difficulty: 'easy',
      confidence: 80,
      estimatedTime: '10 min',
      codeSnippet: generatePropPassingSnippet(violation),
      explanation: 'Convert hardcoded number to dynamic prop. This makes the component reusable with real data.',
      requiredFiles: []
    };
  }
  
  // Case 5: Test emails or test data
  if (description.includes('test') || description.includes('example')) {
    return {
      violation,
      fixType: 'database_query',
      difficulty: 'easy',
      confidence: 95,
      estimatedTime: '5 min',
      codeSnippet: generateAuthUserSnippet(violation),
      explanation: 'Replace test data with real user data from Supabase Auth.',
      requiredFiles: []
    };
  }
  
  // Case 6: Array.fill() or Math.random()
  if (code.includes('Array.fill') || code.includes('Math.random')) {
    return {
      violation,
      fixType: 'database_query',
      difficulty: 'medium',
      confidence: 70,
      estimatedTime: '20 min',
      codeSnippet: generateBulkDataFetchSnippet(violation),
      explanation: 'Replace generated mock data with actual database records. Create the necessary database table if it doesn\'t exist.',
      requiredFiles: ['Database migration needed']
    };
  }
  
  // Default case: Generic fix
  return {
    violation,
    fixType: 'database_query',
    difficulty: 'medium',
    confidence: 60,
    estimatedTime: '15 min',
    codeSnippet: generateGenericDatabaseSnippet(violation),
    explanation: 'Replace mock data with real database query. Review your data model and fetch appropriate data.',
    requiredFiles: []
  };
};

/**
 * Generate database fetch code snippet
 */
const generateDatabaseFetchSnippet = (_violation: MockViolation): string => {
  return `// Step 1: Import Supabase client
import { supabase } from '@/integrations/supabase/client';

// Step 2: Replace useState with empty array
const [data, setData] = useState([]);

// Step 3: Fetch real data on component mount
useEffect(() => {
  const fetchData = async () => {
    const { data: fetchedData, error } = await supabase
      .from('your_table_name') // ⚠️ Change to your actual table
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching data:', error);
      return;
    }
    
    setData(fetchedData || []);
  };
  
  fetchData();
}, []);`;
};

/**
 * Generate count query snippet
 */
const generateCountQuerySnippet = (_violation: MockViolation): string => {
  return `// Fetch real count from database
const [count, setCount] = useState(0);

useEffect(() => {
  const fetchCount = async () => {
    const { count: totalCount, error } = await supabase
      .from('your_table_name') // ⚠️ Change to your actual table
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      console.error('Error fetching count:', error);
      return;
    }
    
    setCount(totalCount || 0);
  };
  
  fetchCount();
}, []);`;
};

/**
 * Generate state-based map snippet
 */
const generateStateBasedMapSnippet = (_violation: MockViolation): string => {
  return `// Step 1: Create state for your data
const [items, setItems] = useState([]);

// Step 2: Fetch data
useEffect(() => {
  const loadItems = async () => {
    const { data, error } = await supabase
      .from('your_table_name')
      .select('*');
    
    if (data) setItems(data);
  };
  loadItems();
}, []);

// Step 3: Use in JSX
{items.map((item) => (
  <div key={item.id}>
    {/* Your content here */}
  </div>
))}`;
};

/**
 * Generate prop passing snippet
 */
const generatePropPassingSnippet = (violation: MockViolation): string => {
  // Try to extract number from description first (more reliable)
  let number = 'VALUE';
  const descMatch = violation.description.match(/number\s+"(\d+)"/i);
  if (descMatch) {
    number = descMatch[1];
  } else if (violation.code) {
    // Fallback: look for standalone numbers in code (not in CSS classes)
    // Match numbers that are NOT preceded by - (like in class names)
    const codeMatch = violation.code.match(/(?<![-\w])(\d+)(?![-\w])/);
    if (codeMatch) {
      number = codeMatch[1];
    }
  }
  
  return `// Step 1: Convert to component prop
interface Props {
  count: number; // Make it dynamic
}

export const YourComponent = ({ count }: Props) => {
  return <div>{count}</div>;
};

// Step 2: Pass real data from parent
<YourComponent count={realCountFromDatabase} />

// Previous hardcoded value: ${number}`;
};

/**
 * Generate auth user snippet
 */
const generateAuthUserSnippet = (_violation: MockViolation): string => {
  return `// Fetch real user data from Supabase Auth
const { data: { user }, error } = await supabase.auth.getUser();

if (user) {
  const email = user.email;
  const name = user.user_metadata?.name;
  // Use real user data here
}`;
};

/**
 * Generate bulk data fetch snippet
 */
const generateBulkDataFetchSnippet = (_violation: MockViolation): string => {
  return `// ❌ Remove mock data generation (Array.fill, Math.random, etc.)

// ✅ Fetch real records from database
const { data, error } = await supabase
  .from('your_table_name')
  .select('*')
  .limit(50); // Adjust limit as needed

// If table doesn't exist yet, create it:
// 1. Go to Supabase Dashboard → SQL Editor
// 2. Create your table with proper columns
// 3. Enable RLS policies for security
// 4. Then fetch real data here`;
};

/**
 * Generate generic database snippet
 */
const generateGenericDatabaseSnippet = (_violation: MockViolation): string => {
  return `// Generic database query template
const { data, error } = await supabase
  .from('your_table_name') // ⚠️ Replace with your table
  .select('*')
  .order('created_at', { ascending: false });

if (error) {
  console.error('Database error:', error);
  return;
}

// Use 'data' in your component
console.log('Fetched data:', data);`;
};

/**
 * Generate all auto-fix suggestions for a list of violations
 */
export const generateAllAutoFixes = (violations: MockViolation[]): AutoFixSuggestion[] => {
  return violations.map(generateAutoFix);
};

/**
 * Group auto-fixes by difficulty for better UX
 */
export const groupByDifficulty = (suggestions: AutoFixSuggestion[]) => {
  return {
    easy: suggestions.filter(s => s.difficulty === 'easy'),
    medium: suggestions.filter(s => s.difficulty === 'medium'),
    hard: suggestions.filter(s => s.difficulty === 'hard')
  };
};
